import Alpine from 'alpinejs'
import collapse from '@alpinejs/collapse'
import fslightbox from 'fslightbox'

import { validate as validateRut } from 'rut.js'

// Import Swiper directly (no code splitting to avoid publicPath issues)
import Swiper from 'swiper'
import {
  Navigation as SwiperNavigation,
  Pagination,
  Thumbs,
  FreeMode,
  Autoplay,
} from 'swiper/modules'

// Import Swiper CSS
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/thumbs'
import 'swiper/css/free-mode'
import 'swiper/css/autoplay'

import {
  PredictiveSearch,
  SearchForm,
  CartNotification,
  ProductForm,
  CartItems,
  CartRemoveButton,
  CartNote,
  QuantityPopover,
  QuantityInput,
  ProductInfo,
  ProductRecommendations,
  ShowMoreButton,
  VariantSelects,
  ProductCardMedia,
} from './web-components'
import { CartDrawer, CartDrawerItems } from './web-components/CartDrawer'
import { HomeSlideshow } from './web-components/HomeSlideshow'
import { SwiperCarousel } from './web-components/SwiperCarousel'
import './utils/globalCountdown' // Initialize global countdowns
import { initProductCardSwatchInteractions } from './productCardSwatchInteractions'

//Alpine plugins
Alpine.plugin(collapse)

//window global variables
window.validateRut = validateRut

// Set global variables for Swiper (for backward compatibility with Alpine.js sections)
window.Swiper = Swiper
window.SwiperNavigation = SwiperNavigation
window.Pagination = Pagination
window.Thumbs = Thumbs
window.FreeMode = FreeMode
window.Autoplay = Autoplay

/**
 * Initialize Swiper carousels using data attributes
 * This replaces Alpine.js carousels to avoid timing issues
 */
function initSwiperCarousels() {
  console.log('🔍 [Swiper] initSwiperCarousels - Iniciando búsqueda de carousels')
  if (!window.Swiper) {
    console.warn('⚠️ [Swiper] Swiper not available yet, retrying...')
    setTimeout(initSwiperCarousels, 100)
    return
  }

  const carousels = document.querySelectorAll('[data-swiper-carousel]')
  console.log(`🔍 [Swiper] Encontrados ${carousels.length} carousel(s) con data-swiper-carousel`)

  carousels.forEach((container, index) => {
    console.log(`\n📦 [Swiper] Procesando carousel ${index + 1}/${carousels.length}`)
    console.log('📦 [Swiper] Container:', container)
    console.log('📦 [Swiper] Container ID:', container.id || 'sin ID')
    console.log('📦 [Swiper] Container classes:', container.className)
    console.log('📦 [Swiper] Container dataset:', {
      swiperCarousel: container.dataset.swiperCarousel,
      swiperModules: container.dataset.swiperModules,
      swiperLoop: container.dataset.swiperLoop,
      swiperSlidesPerView: container.dataset.swiperSlidesPerView,
      swiperSpaceBetween: container.dataset.swiperSpaceBetween,
      swiperAutoplayDelay: container.dataset.swiperAutoplayDelay,
      swiperInitialized: container.dataset.swiperInitialized,
    })

    // Skip if already initialized
    if (container.dataset.swiperInitialized === 'true') {
      console.log('⏭️ [Swiper] Carousel ya inicializado, saltando...')
      return
    }

    const config = {
      modules: [],
      // Swiper default values - matching original Alpine.js configuration
      speed: 300, // Default transition speed in ms (same as Swiper default, matching original)
      direction: 'horizontal',
      effect: 'slide',
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      // Ensure CSS transitions are enabled (not cssMode)
      cssMode: false,
      // Enable smooth transitions
      allowTouchMove: true,
      // Prevent navigation buttons from being disabled
      watchOverflow: false,
    }

    console.log('⚙️ [Swiper] Configuración inicial:', config)

    // Parse modules from data attribute (comma-separated: navigation,pagination,autoplay)
    const modulesStr = container.dataset.swiperModules || ''
    console.log('📋 [Swiper] Modules string:', modulesStr)

    if (modulesStr.includes('navigation')) {
      config.modules.push(SwiperNavigation)
      console.log('✅ [Swiper] Módulo Navigation agregado')
    }
    if (modulesStr.includes('pagination')) {
      config.modules.push(Pagination)
      console.log('✅ [Swiper] Módulo Pagination agregado')
    }
    if (modulesStr.includes('autoplay')) {
      config.modules.push(Autoplay)
      console.log('✅ [Swiper] Módulo Autoplay agregado')
    }
    if (modulesStr.includes('thumbs')) {
      config.modules.push(Thumbs)
      console.log('✅ [Swiper] Módulo Thumbs agregado')
    }
    if (modulesStr.includes('freeMode')) {
      config.modules.push(FreeMode)
      console.log('✅ [Swiper] Módulo FreeMode agregado')
    }

    console.log('📦 [Swiper] Módulos totales:', config.modules.length)

    // Override defaults with data attributes if present
    console.log('🔧 [Swiper] Aplicando atributos data-...')
    if (container.dataset.swiperSpeed !== undefined) {
      config.speed = parseInt(container.dataset.swiperSpeed, 10)
      console.log(`  ⚙️ speed: ${config.speed}`)
    }
    if (container.dataset.swiperLoop !== undefined) {
      config.loop = container.dataset.swiperLoop === 'true'
      console.log(`  ⚙️ loop: ${config.loop}`)
    }
    if (container.dataset.swiperSlidesPerView !== undefined) {
      config.slidesPerView = parseFloat(container.dataset.swiperSlidesPerView)
      console.log(`  ⚙️ slidesPerView: ${config.slidesPerView}`)
    }
    if (container.dataset.swiperSpaceBetween !== undefined) {
      config.spaceBetween = parseInt(container.dataset.swiperSpaceBetween, 10)
      console.log(`  ⚙️ spaceBetween: ${config.spaceBetween}`)
    }
    if (container.dataset.swiperCenteredSlides !== undefined) {
      config.centeredSlides = container.dataset.swiperCenteredSlides === 'true'
      console.log(`  ⚙️ centeredSlides: ${config.centeredSlides}`)
    }
    if (container.dataset.swiperDirection !== undefined) {
      config.direction = container.dataset.swiperDirection
      console.log(`  ⚙️ direction: ${config.direction}`)
    }
    if (container.dataset.swiperEffect !== undefined) {
      config.effect = container.dataset.swiperEffect
      console.log(`  ⚙️ effect: ${config.effect}`)
    }

    // Autoplay configuration
    if (
      modulesStr.includes('autoplay') &&
      container.dataset.swiperAutoplayDelay
    ) {
      config.autoplay = {
        delay: parseInt(container.dataset.swiperAutoplayDelay, 10),
        // Match original Alpine.js config: disableOnInteraction: false
        disableOnInteraction: false,
      }
      console.log('  ⚙️ autoplay:', config.autoplay)
    }

    // Breakpoints configuration (from JSON data attribute)
    if (container.dataset.swiperBreakpoints) {
      try {
        config.breakpoints = JSON.parse(container.dataset.swiperBreakpoints)
        console.log('  ⚙️ breakpoints:', config.breakpoints)
      } catch (e) {
        console.warn('⚠️ [Swiper] Invalid breakpoints JSON:', e)
      }
    }

    // Find the carousel container
    // Swiper needs the direct parent of swiper-wrapper to be initialized
    console.log('🔍 [Swiper] Buscando swiper-wrapper...')
    const swiperWrapper = container.querySelector('.swiper-wrapper')
    console.log('🔍 [Swiper] swiper-wrapper encontrado:', swiperWrapper)
    
    let carouselContainer = swiperWrapper?.parentElement || container
    console.log('🔍 [Swiper] carouselContainer inicial:', carouselContainer)
    console.log('🔍 [Swiper] carouselContainer classes:', carouselContainer.className)

    // If the container has 'swiper' class but wrapper is in a child, use the child
    // Otherwise ensure the container has swiper class
    if (
      container.classList.contains('swiper') &&
      swiperWrapper &&
      swiperWrapper.parentElement !== container
    ) {
      // Wrapper is in a child div, use that child as the Swiper container
      carouselContainer = swiperWrapper.parentElement
      console.log('🔍 [Swiper] Wrapper está en un hijo, usando:', carouselContainer)
      if (!carouselContainer.classList.contains('swiper')) {
        carouselContainer.classList.add('swiper')
        console.log('✅ [Swiper] Clase "swiper" agregada al carouselContainer')
      }
    } else if (!carouselContainer.classList.contains('swiper')) {
      carouselContainer.classList.add('swiper')
      console.log('✅ [Swiper] Clase "swiper" agregada al carouselContainer')
    }
    
    console.log('📦 [Swiper] carouselContainer final:', carouselContainer)
    console.log('📦 [Swiper] carouselContainer classes final:', carouselContainer.className)

    // Navigation configuration - search after determining carouselContainer
    if (modulesStr.includes('navigation')) {
      // Search for navigation buttons within the entire container and carouselContainer
      // Also search in parent elements to find buttons that might be outside the carouselContainer
      const nextEl =
        container.querySelector('[data-swiper-next]') ||
        carouselContainer.querySelector('[data-swiper-next]') ||
        container.closest('section')?.querySelector('[data-swiper-next]')
      const prevEl =
        container.querySelector('[data-swiper-prev]') ||
        carouselContainer.querySelector('[data-swiper-prev]') ||
        container.closest('section')?.querySelector('[data-swiper-prev]')

      console.log('🔍 [Swiper] Navigation buttons search:', {
        container: container,
        carouselContainer: carouselContainer,
        nextEl: nextEl,
        prevEl: prevEl,
        hasNavigationModule: modulesStr.includes('navigation'),
      })

      if (nextEl || prevEl) {
        config.navigation = {
          nextEl: nextEl || null,
          prevEl: prevEl || null,
        }
        console.log('✅ [Swiper] Navigation configured:', config.navigation)
      } else {
        console.warn(
          '⚠️ [Swiper] Navigation module enabled but no buttons found'
        )
      }
    }

    // Pagination configuration - search after determining carouselContainer
    if (modulesStr.includes('pagination')) {
      const paginationEl =
        container.querySelector('[data-swiper-pagination]') ||
        carouselContainer.querySelector('[data-swiper-pagination]')
      if (paginationEl) {
        config.pagination = {
          el: paginationEl,
          clickable: container.dataset.swiperPaginationClickable !== 'false',
        }
      }
    }

    // Initialize Swiper
    try {
      // Function to remove x-cloak from carousel elements
      // This should only be called AFTER Swiper is fully initialized to prevent layout shift
      const removeXCloak = () => {
        // Remove x-cloak from all slides
        const slides = container.querySelectorAll('.swiper-slide[x-cloak]')
        slides.forEach((slide) => {
          slide.removeAttribute('x-cloak')
        })

        // Also remove x-cloak from navigation buttons and pagination inside the carousel
        const navButtons = container.querySelectorAll(
          '[data-swiper-prev][x-cloak], [data-swiper-next][x-cloak]'
        )
        navButtons.forEach((btn) => {
          btn.removeAttribute('x-cloak')
        })

        const pagination = container.querySelectorAll(
          '[data-swiper-pagination][x-cloak]'
        )
        pagination.forEach((el) => {
          el.removeAttribute('x-cloak')
        })
      }

      // Function to hide loader
      const hideLoader = () => {
        const loader = container.querySelector('[data-swiper-loader]')
        if (loader) {
          loader.style.opacity = '0'
          loader.style.pointerEvents = 'none'
          // Remove loader from DOM after transition
          setTimeout(() => {
            loader.remove()
          }, 300)
        }
      }

      // Function to hide skeleton and show products
      const hideSkeleton = () => {
        const skeleton = container.querySelector('[data-swiper-skeleton]')
        // Select the ul.swiper-wrapper (products), not the skeleton's div
        const productsWrapper = container.querySelector('ul.swiper-wrapper')

        if (skeleton) {
          skeleton.style.opacity = '0'
          skeleton.style.pointerEvents = 'none'
          // Remove skeleton from DOM after transition
          setTimeout(() => {
            skeleton.remove()
          }, 300)
        }

        // Show products wrapper
        if (productsWrapper) {
          productsWrapper.style.opacity = '1'
          productsWrapper.style.transition = 'opacity 0.3s ease'
        }
      }

      // Merge existing on callbacks with our init callback
      const existingOnCallbacks = config.on || {}
      config.on = {
        ...existingOnCallbacks,
        init: function () {
          // Call existing init callback if it exists
          if (existingOnCallbacks.init) {
            existingOnCallbacks.init.call(this)
          }
          // Remove x-cloak AFTER Swiper is fully initialized
          // Use requestAnimationFrame to ensure DOM is ready
          requestAnimationFrame(() => {
            removeXCloak()
            // Hide loader after Swiper is ready
            hideLoader()
            // Hide skeleton and show products
            hideSkeleton()
          })
        },
        slideChange: function () {
          // Ensure navigation buttons remain enabled after slide changes
          // This is especially important with loop enabled
          if (existingOnCallbacks.slideChange) {
            existingOnCallbacks.slideChange.call(this)
          }
        },
        reachEnd: function () {
          // When loop is enabled, buttons should always work
          if (config.loop) {
            // Force enable buttons when loop is active
            const nextBtn = this.navigation?.nextEl
            const prevBtn = this.navigation?.prevEl
            if (nextBtn) nextBtn.classList.remove('swiper-button-disabled')
            if (prevBtn) prevBtn.classList.remove('swiper-button-disabled')
          }
          if (existingOnCallbacks.reachEnd) {
            existingOnCallbacks.reachEnd.call(this)
          }
        },
        reachBeginning: function () {
          // When loop is enabled, buttons should always work
          if (config.loop) {
            // Force enable buttons when loop is active
            const nextBtn = this.navigation?.nextEl
            const prevBtn = this.navigation?.prevEl
            if (nextBtn) nextBtn.classList.remove('swiper-button-disabled')
            if (prevBtn) prevBtn.classList.remove('swiper-button-disabled')
          }
          if (existingOnCallbacks.reachBeginning) {
            existingOnCallbacks.reachBeginning.call(this)
          }
        },
      }

      console.log('🚀 [Swiper] Inicializando Swiper con config:', config)
      console.log('🚀 [Swiper] carouselContainer:', carouselContainer)
      
      const swiper = new Swiper(carouselContainer, config)
      
      console.log('✅ [Swiper] Swiper inicializado exitosamente')
      console.log('✅ [Swiper] Instancia Swiper:', swiper)
      console.log('✅ [Swiper] Navigation:', swiper.navigation)
      console.log('✅ [Swiper] Navigation nextEl:', swiper.navigation?.nextEl)
      console.log('✅ [Swiper] Navigation prevEl:', swiper.navigation?.prevEl)
      console.log('✅ [Swiper] Slides totales:', swiper.slides?.length)
      console.log('✅ [Swiper] Active index:', swiper.activeIndex)
      
      container.dataset.swiperInitialized = 'true'
      console.log('✅ [Swiper] Marcado como inicializado')

      // Don't remove x-cloak immediately - wait for Swiper's init callback
      // This prevents layout shift while JavaScript is loading
    } catch (error) {
      console.error('❌ [Swiper] Error initializing Swiper carousel:', error)
      console.error('❌ [Swiper] Container:', container)
      console.error('❌ [Swiper] Config:', config)
      console.error('❌ [Swiper] Stack:', error.stack)
      // Hide loader even if there's an error
      const loader = container.querySelector('[data-swiper-loader]')
      if (loader) {
        loader.remove()
      }
    }
    
    console.log(`\n✅ [Swiper] Carousel ${index + 1} procesado\n`)
  })
  
  console.log('✅ [Swiper] initSwiperCarousels completado')
}

document.addEventListener('DOMContentLoaded', () => {
  // Refrescar fslightbox para detectar nuevos elementos
  refreshFsLightbox()

  // Initialize Swiper carousels
  initSwiperCarousels()
})

// Also initialize on dynamic content updates (for Shopify sections)
if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', initSwiperCarousels)
  document.addEventListener('shopify:section:reorder', initSwiperCarousels)
}

//AlpineJS declarations
window.Alpine = Alpine

// Swiper is now loaded directly, so we can start Alpine immediately
// Wait for DOM to be ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    Alpine.start()
  })
} else {
  // DOM already ready, start Alpine immediately
  Alpine.start()
}

// Define all web componentes of the site
customElements.define('predictive-search', PredictiveSearch)
customElements.define('search-form', SearchForm)
customElements.define('cart-notification', CartNotification)
customElements.define('cart-remove-button', CartRemoveButton)
customElements.define('cart-items', CartItems)
customElements.define('quantity-input', QuantityInput)
customElements.define('product-recommendations', ProductRecommendations)
customElements.define('variant-selects', VariantSelects)
customElements.define('cart-drawer', CartDrawer)
customElements.define('cart-drawer-items', CartDrawerItems)

//validate if is exist
// product form
if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm)
}

//cart note
if (!customElements.get('cart-note')) {
  customElements.define('cart-note', CartNote)
}
//quantity popover
if (!customElements.get('quantity-popover')) {
  customElements.define('quantity-popover', QuantityPopover)
}

//Product Info
if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo)
}

//Show More button
if (!customElements.get('show-more-button')) {
  customElements.define('show-more-button', ShowMoreButton)
}

//Product Card Media
if (!customElements.get('product-card-media')) {
  customElements.define('product-card-media', ProductCardMedia)
}

//Home Slideshow
if (!customElements.get('home-slideshow')) {
  customElements.define('home-slideshow', HomeSlideshow)
}

//Swiper Carousel (generic)
if (!customElements.get('swiper-carousel')) {
  customElements.define('swiper-carousel', SwiperCarousel)
}

initProductCardSwatchInteractions()

// Handle candy rack add to cart - open cart drawer instead of notification
document.addEventListener(
  'ppp-candyrack-button-layout-offers-added-to-cart',
  async (e) => {
    try {
      console.log('candyrack-button-layout-offers-added-to-cart', e)
      // Get cart drawer element
      const cartDrawer = document.querySelector('cart-drawer')
      if (!cartDrawer) return

      // Check if cart was empty before this addition
      const wasEmpty = cartDrawer.classList.contains('is-empty')
      console.log('Cart was empty:', wasEmpty)

      // Get cart data
      const cartResponse = await fetch('/cart.js')
      const cart = await cartResponse.json()

      console.log('CART: ', cart)

      // Get variant ID from the event's product data
      const eventVariantId = e.detail?.product?.variants?.[0]?.id
      console.log('Event variant ID:', eventVariantId)

      // Find the correct item in cart by matching variant ID
      const cartItem = cart.items.find(
        (item) => item.variant_id.toString() === eventVariantId?.toString()
      )

      // If we can't find the item, fallback to last item
      const lastItem = cartItem || cart.items[cart.items.length - 1]
      console.log('Found cart item:', lastItem)

      // Fetch cart drawer sections (same as CartDrawer.getSectionsToRender())
      const sections = {}
      const sectionsToFetch = ['cart-drawer', 'cart-icon-bubble']

      await Promise.all(
        sectionsToFetch.map(async (section) => {
          const response = await fetch(`?section_id=${section}`)
          if (response.ok) {
            const html = await response.text()
            sections[section] = html
          }
        })
      )

      // Create the complete parsedState object
      const parsedState = {
        ...lastItem,
        sections,
      }

      console.log('parsedState', parsedState)

      // Handle empty to non-empty transition
      if (wasEmpty && cart.item_count > 0) {
        console.log('Transitioning from empty to first item')

        // Remove is-empty class from cart drawer
        cartDrawer.classList.remove('is-empty')

        // Remove is-empty class from cart drawer items if it exists
        const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items')
        if (cartDrawerItems) {
          cartDrawerItems.classList.remove('is-empty')
        }
      }

      // Render the cart drawer contents and open it
      cartDrawer.renderContents(parsedState)
    } catch (error) {
      console.error('Error handling candyrack event:', error)
      // Still try to open cart drawer even if update fails
      const cartDrawer = document.querySelector('cart-drawer')
      if (cartDrawer) cartDrawer.open()
    }
  }
)
